import { Col, Row } from "antd";
import cuid from "cuid";
import { useFormik } from "formik";
import { get } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineLink } from "react-icons/ai";
import {
  MdArrowBackIos,
  MdArrowForwardIos,
  MdFileUpload,
  MdOutlineBadge,
  MdOutlineDescription,
  MdOutlineHome,
  MdPayment,
} from "react-icons/md";
import GenerateLinkModal from "screens/Private/Broker/Projects/List/Subscriptions/GenerateLinkModal";
import Alert from "shared/components/Alert";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import {
  PPEGELControlSection,
  PPEGELDrawer,
  PPEGELProvider,
} from "shared/components/PPEGEL";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import format from "string-template";
import {
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
} from "utils/api/graphql/mutations/attachment";
import { SUBSCRIPTION as SUBSCRIPTION_QUERY } from "utils/api/graphql/queries/subscription";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  ACORIS_PROFILES,
  ATTACHMENT_TYPES,
  BANK_ACCOUNT_OWNER_TYPE,
  RELATIONSHIP,
  SUBSCRIPTION_FORM_KEYS,
  SUBSCRIPTION_STATUSES,
  THEMES_PALETTE,
} from "utils/constants";
import { normalizeAcorisProfile } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/insurancePlan";
import { getAgeFromBirthDate } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/relatedPersonChild";
import { STEPPER_KEYS } from "../..";
import Progress from "../Progress";
import generateProofForm from "./Forms";
import validation from "./validation";

const CHILD_ID_AGE_THRESHOLD = 18;

// Prospect profiles that trigger the conditional "Justificatif d'éligibilité".
const ELIGIBILITY_PROFILES = [
  ACORIS_PROFILES.TERRITORIAL_AGENT,
  ACORIS_PROFILES.TNS,
  ACORIS_PROFILES.CROSS_BORDER_WORKER,
];

export const DOCUMENTS_TYPE = {
  cinRecto: ATTACHMENT_TYPES.CIN_RECTO,
  cinVerso: ATTACHMENT_TYPES.CIN_VERSO,
  spouseCinRecto: ATTACHMENT_TYPES.CIN_RECTO,
  spouseCinVerso: ATTACHMENT_TYPES.CIN_VERSO,
  payerCinRecto: ATTACHMENT_TYPES.PAYER_CIN_RECTO,
  payerCinVerso: ATTACHMENT_TYPES.PAYER_CIN_VERSO,
  paymentAccount: ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
  refundAccount: ATTACHMENT_TYPES.REFUND_ACCOUNT,
  socialSecurityCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  proofOfAddress: ATTACHMENT_TYPES.PROOF_OF_ADDRESS,
  proofOfEligibility: ATTACHMENT_TYPES.PROOF_OF_ELIGIBILITY,
  contributionPaymentAgreement: ATTACHMENT_TYPES.CONTRIBUTION_PAYMENT_AGREEMENT,
  spouseSocialSecurityCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  // Per-child documents handled dynamically:
  //  childSocialSecurityCertificate_{userId} → HEALTH_INSURANCE_RIGHTS_CERTIFICATE
  //  childCinRecto_{userId} / childCinVerso_{userId} → CIN_RECTO / CIN_VERSO
  //  childStudentCertificate_{userId} → STUDENT_ID_OR_ENROLLMENT_CERTIFICATE
};

const ICONS = {
  [SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE]: (
    <MdOutlineDescription size={16} />
  ),
  [SUBSCRIPTION_FORM_KEYS.PROOF.ID]: <MdOutlineBadge size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.BANK_ACCOUNT]: <MdPayment size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER_RIB]: <MdPayment size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.RECEIVER_RIB]: <MdPayment size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ADDRESS]: <MdOutlineHome size={16} />,
  [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ELIGIBILITY]: (
    <MdFileUpload size={16} />
  ),
  [SUBSCRIPTION_FORM_KEYS.PROOF.CONTRIBUTION_PAYMENT_AGREEMENT]: (
    <MdFileUpload size={16} />
  ),
  [SUBSCRIPTION_FORM_KEYS.PROOF.STUDENT_CERTIFICATE]: (
    <MdFileUpload size={16} />
  ),
};

const Proof = ({
  subscription,
  progress,
  setPercent,
  disabled,
  initialValues,
  setActive,
  calculateProgressMean,
  isSubmitting,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { user } = useContext(StoreContext);
  const hasEfficialConfig = !!get(
    user,
    "organization.config.EFFICIAL_CLIENT_ID",
  );
  const [addAttachment, { loading }] = useMutation(CREATE_ATTACHMENT);
  const [getTracer] = useLazyQuery(TRACER);

  const refetchQueries = useMemo(
    () => [
      {
        query: SUBSCRIPTION_QUERY,
        awaitRefetchQueries: true,
        variables: {
          where: { id: subscription.id },
        },
      },
    ],
    [subscription.id],
  );

  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT, {
    refetchQueries,
  });

  const [open, setOpen] = useState({});

  const onOpenGenerateLink = (data) => setOpen(data);

  // Determine payment conditions based on subscription data
  const paymentConditions = useMemo(() => {
    const payment = get(subscription, "payment");

    if (!payment) {
      return {
        isPayerReceiver: true,
        payerIsAdherent: true,
        receiverIsAdherent: true,
        requiresSubscriberPaysButDifferentReceiver: false,
        requiresThirdPartyPayer: false,
        requiresPayerCin: false,
        requiresBothThirdParty: false,
        requiresTwoSeparateRibs: false,
        isPayerOrganization: false,
      };
    }

    // Check if payer and receiver are the same account
    const receiverId = get(payment, "receiver.id");
    const payerId = get(payment, "payer.id");
    const isPayerReceiver = receiverId === payerId;

    // Check if payer is the adherent (subscriber)
    const payerIsAdherent = get(payment, "payer.owner.isAdherent", true);

    // Check if receiver is the adherent (subscriber)
    const receiverIsAdherent = get(payment, "receiver.owner.isAdherent", true);

    // Check if payer is an organization (autre payeur + switch organization)
    const payerType = get(payment, "payer.owner.type");
    const isPayerOrganization =
      payerType === BANK_ACCOUNT_OWNER_TYPE.COMPANY && !payerIsAdherent;

    // Subscriber pays premiums but doesn't receive reimbursements
    const requiresSubscriberPaysButDifferentReceiver =
      payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;

    // Third-party payer: Subscriber doesn't pay premiums (someone else pays)
    const requiresThirdPartyPayer = !payerIsAdherent;

    // Payer CIN required when third-party payer is a particular person (not an organization)
    const requiresPayerCin = !payerIsAdherent && !isPayerOrganization;

    // Subscriber doesn't pay AND doesn't receive
    const requiresBothThirdParty =
      !payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;

    // Requires 2 separate RIBs when payer and receiver are different accounts
    const requiresTwoSeparateRibs = !isPayerReceiver;

    return {
      isPayerReceiver,
      payerIsAdherent,
      receiverIsAdherent,
      requiresSubscriberPaysButDifferentReceiver,
      requiresThirdPartyPayer,
      requiresPayerCin,
      requiresBothThirdParty,
      requiresTwoSeparateRibs,
      isPayerOrganization,
    };
  }, [subscription]);

  // Eligibility justificatif depends on the prospect profile (JSON parcours).
  const requiresEligibilityProof = useMemo(() => {
    const profileType = normalizeAcorisProfile(
      get(subscription, "project.contact.fields.profile") ||
        get(subscription, "project.fields.profileType"),
    );
    return ELIGIBILITY_PROFILES.includes(profileType);
  }, [subscription]);

  // Clean up attachments when conditions change
  const cleanupObsoleteAttachments = useCallback(async () => {
    const attachments = get(subscription, "attachments", []);
    const { requiresTwoSeparateRibs, requiresThirdPartyPayer } =
      paymentConditions;

    const relatedPersons = get(
      subscription,
      "project.contact.relatedPersons",
      [],
    );
    const hasSpouse = relatedPersons.some(
      (p) => p?.relationship === RELATIONSHIP.SPOUSE,
    );
    const children = relatedPersons.filter(
      (p) => p?.relationship === RELATIONSHIP.CHILD,
    );
    const childUserIds = children.map((c) => get(c, "identity.user.id"));
    const spouseUserId = get(
      relatedPersons.find((p) => p?.relationship === RELATIONSHIP.SPOUSE),
      "identity.user.id",
    );
    const adherentUserId = get(subscription, "project.contact.user.id");

    const attachmentsToDelete = [];

    if (!requiresTwoSeparateRibs) {
      attachments
        .filter((att) => att.type === ATTACHMENT_TYPES.REFUND_ACCOUNT)
        .forEach((att) => attachmentsToDelete.push(att.id));
    }

    if (!requiresThirdPartyPayer) {
      attachments
        .filter(
          (att) =>
            att.type === ATTACHMENT_TYPES.PAYER_CIN ||
            att.type === ATTACHMENT_TYPES.PAYER_CIN_RECTO ||
            att.type === ATTACHMENT_TYPES.PAYER_CIN_VERSO ||
            att.type === ATTACHMENT_TYPES.CONTRIBUTION_PAYMENT_AGREEMENT,
        )
        .forEach((att) => attachmentsToDelete.push(att.id));
    }

    if (!hasSpouse && spouseUserId) {
      const spouseAttachmentTypes = [
        ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
        ATTACHMENT_TYPES.CIN,
        ATTACHMENT_TYPES.CIN_RECTO,
        ATTACHMENT_TYPES.CIN_VERSO,
      ];
      attachments
        .filter((att) => {
          const attUserId = get(att, "user.id") || get(att, "userId");
          return (
            attUserId === spouseUserId &&
            spouseAttachmentTypes.includes(att.type)
          );
        })
        .forEach((att) => attachmentsToDelete.push(att.id));
    }

    attachments
      .filter((att) => {
        const attUserId = get(att, "user.id") || get(att, "userId");
        return (
          att.type === ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE &&
          attUserId !== adherentUserId &&
          attUserId !== spouseUserId &&
          !childUserIds.includes(attUserId)
        );
      })
      .forEach((att) => attachmentsToDelete.push(att.id));

    const uniqueIds = [...new Set(attachmentsToDelete)];
    if (uniqueIds.length > 0) {
      await Promise.all(
        uniqueIds.map((id) =>
          deleteAttachment({
            variables: { where: { id } },
          }),
        ),
      );
    }
  }, [subscription, paymentConditions, deleteAttachment]);

  useEffect(() => {
    cleanupObsoleteAttachments();
  }, [cleanupObsoleteAttachments]);

  // Helper function to get the correct user ID based on document type
  const getUserIdForDocument = (key) => {
    // Adherent user ID
    const adherentUserId = get(subscription, "project.contact.user.id");

    // Spouse user ID
    const relatedPersons = get(
      subscription,
      "project.contact.relatedPersons",
      [],
    );
    const spouse = relatedPersons.find(
      (person) => person?.relationship === RELATIONSHIP.SPOUSE,
    );
    const spouseUserId = get(spouse, "identity.user.id");

    // Payer user ID (if different from adherent)
    const payerUserId = get(subscription, "payment.payer.owner.user.id");

    // Receiver user ID (if different from adherent)
    const receiverUserId = get(subscription, "payment.receiver.owner.user.id");

    // Children documents - extract user ID from the dynamic key suffix.
    if (key.startsWith("childSocialSecurityCertificate_")) {
      return key.replace("childSocialSecurityCertificate_", "");
    }
    if (key.startsWith("childCinRecto_")) {
      return key.replace("childCinRecto_", "");
    }
    if (key.startsWith("childCinVerso_")) {
      return key.replace("childCinVerso_", "");
    }
    if (key.startsWith("childStudentCertificate_")) {
      return key.replace("childStudentCertificate_", "");
    }

    if (
      key === "spouseSocialSecurityCertificate" ||
      key === "spouseCinRecto" ||
      key === "spouseCinVerso"
    ) {
      return spouseUserId;
    }

    if (
      key === "payerCinRecto" ||
      key === "payerCinVerso" ||
      key === "contributionPaymentAgreement"
    ) {
      return payerUserId || adherentUserId;
    }

    // RIB cotisations → tiers payeur when payeur ≠ adhérent.
    if (key === "paymentAccount") {
      const payerIsAdherent = get(
        subscription,
        "payment.payer.owner.isAdherent",
        true,
      );
      return !payerIsAdherent && payerUserId ? payerUserId : adherentUserId;
    }

    if (key === "refundAccount") {
      return receiverUserId || adherentUserId;
    }

    return adherentUserId;
  };

  const onSubmit = async (data) => {
    const documents = get(data, "proof", {});
    const documentsToUpload = [];
    Object.entries(documents)?.map(([key, attachments]) =>
      attachments
        ?.filter((e) => !e.status)
        .forEach((document) => {
          document.status = true;

          // Determine document type; per-child keys map to shared attachment types.
          let documentType = DOCUMENTS_TYPE[key];
          if (key.startsWith("childSocialSecurityCertificate_")) {
            documentType = ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE;
          } else if (key.startsWith("childCinRecto_")) {
            documentType = ATTACHMENT_TYPES.CIN_RECTO;
          } else if (key.startsWith("childCinVerso_")) {
            documentType = ATTACHMENT_TYPES.CIN_VERSO;
          } else if (key.startsWith("childStudentCertificate_")) {
            documentType =
              ATTACHMENT_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE;
          }

          // Get the appropriate user ID for this document
          const userId = getUserIdForDocument(key);

          documentsToUpload.push({
            id: cuid(),
            subscription: { id: get(subscription, "id") },
            ...(userId && { user: { id: userId } }),
            name: document.name,
            content: document.base64,
            contentType: document.contentType,
            type: documentType,
          });
        }),
    );
    await Promise.all(
      documentsToUpload.map((document, index) =>
        addAttachment({
          variables: {
            data: document,
          },
          refetchQueries:
            index === documentsToUpload?.length - 1 && refetchQueries,
        }),
      ),
    );

    calculateProgressMean(STEPPER_KEYS.PROOF, data);
    setActive(STEPPER_KEYS.DOCUMENT);
  };

  const { isValid, handleSubmit, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues,
      onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
          returnObjects: true,
        }),
      ),
    });

  useEffect(() => {
    calculateProgressMean(STEPPER_KEYS.PROOF, values);
  }, [calculateProgressMean, values]);

  const relatedPersons = get(
    subscription,
    "project.contact.relatedPersons",
    [],
  ).filter(Boolean);
  const hasAdultChild = relatedPersons.some(
    (person) =>
      person?.relationship === RELATIONSHIP.CHILD &&
      getAgeFromBirthDate(get(person, "identity.user.birthDate")) >=
        CHILD_ID_AGE_THRESHOLD,
  );

  const formList = useMemo(
    () =>
      Object.entries(
        generateProofForm({
          hasAdultChild,
          requiresEligibilityProof,
          requiresThirdPartyPayer: paymentConditions.requiresThirdPartyPayer,
        }),
      ).filter(([_, Form]) => Form),
    [hasAdultChild, requiresEligibilityProof, paymentConditions],
  );
  const onFileRemove = async ({ uid: id }) => {
    if (id.indexOf("AUTO") !== -1) return;
    return deleteAttachment({
      variables: {
        where: {
          id,
        },
      },
    });
  };

  return (
    <div key="proof" className="subscription-proof--wrapper">
      <GenerateLinkModal
        open={open}
        setOpen={setOpen}
        template={get(subscription, "project.fields.processTemplate")}
        tracerId={open?.tracerId}
      />

      <Row gutter={[16, 16]}>
        <Col lg={24} xs={24}>
          <Row align="middle" gutter={[16, 16]}>
            <Col lg={18} xs={24}>
              <Alert
                type="warning"
                showIcon
                message={SUBSCRIPTION.PROOF.WARNING}
              />
            </Col>
            <Col align="end" lg={6} xs={24}>
              <span className="indicator-step">
                {format(SUBSCRIPTION.INDICATOR, { count: "2", total: "3" })}
              </span>
            </Col>
          </Row>
        </Col>
        <Col lg={24} xs={24}>
          <Row gutter={[16, 16]}>
            <Col lg={9} xs={24}>
              <div className="card-skeleton--container highlighted">
                <div className="card-skeleton--wrapper">
                  <h3 className="title--md">{SUBSCRIPTION.PROOF.TITLE}</h3>
                  <div className="card-skeleton--wrapper__progress-list">
                    {formList.map(([form], index) => (
                      <Progress
                        key={"progress-" + index}
                        percent={progress["PROOF"][form]}
                        status={progress["PROOF"][form]}
                        translation={SUBSCRIPTION}
                        icon={ICONS[form]}
                        title={SUBSCRIPTION.PROOF.STEPS[form]}
                      />
                    ))}
                  </div>
                </div>
                <Button
                  type={"primary"}
                  size="full--width"
                  onClick={async () => {
                    const {
                      data: {
                        tracer: { source },
                      },
                    } = await getTracer({
                      variables: {
                        where: {
                          flow: {
                            process: {
                              theme:
                                THEMES_PALETTE[
                                  get(
                                    subscription,
                                    "project.fields.processTemplate",
                                  )
                                ].PROSPECT_PROCESS,
                            },
                          },
                        },
                      },
                    });
                    onOpenGenerateLink({
                      projectId: get(subscription, "project.id"),
                      tracerId: get(subscription, "project.tracer.id"),
                      subscriptionId: subscription.id,
                      insurancePlanIds: [get(subscription, "insurancePlan.id")],
                      isInfoCompleted: get(
                        subscription,
                        "additionalInfo.isInfoCompleted",
                      ),
                      isSigned:
                        get(subscription, "status") ===
                        SUBSCRIPTION_STATUSES.SIGNED,
                      baseUrl: get(
                        subscription,
                        "insurancePlan.config.generatedUrl",
                        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source.id}`,
                      ),
                    });
                  }}
                  disabled={isSubmitting}
                >
                  {SUBSCRIPTION.GENERATE_LINK}
                  <AiOutlineLink size={24} />
                </Button>
              </div>
            </Col>
            {loading ? (
              <Loading />
            ) : (
              <Col lg={15} xs={24} gutter={16}>
                {hasEfficialConfig && (
                  <PPEGELProvider
                    projectId={get(subscription, "project.id")}
                    subscription={subscription}
                  >
                    <PPEGELControlSection />
                    <PPEGELDrawer />
                  </PPEGELProvider>
                )}
                <Card
                  title={SUBSCRIPTION.PROOF.TITLE}
                  subtitle={SUBSCRIPTION.PROOF.SUBTITLE}
                >
                  {formList.map(([key, Form], index) => {
                    return (
                      <Form
                        key={"form-" + index}
                        disabled={disabled}
                        subscription={subscription}
                        values={values}
                        formProps={{ ...formProps }}
                        setFieldValue={setFieldValue}
                        onFileRemove={onFileRemove}
                        setPercent={(data) => {
                          setPercent(STEPPER_KEYS.PROOF, key, data);
                        }}
                        paymentConditions={paymentConditions}
                      />
                    );
                  })}
                </Card>
                <div className="d-flex justify--between m-top-16">
                  <Button
                    type="secondary--link"
                    onClick={() =>
                      setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION)
                    }
                  >
                    <MdArrowBackIos size={16} />
                    {SUBSCRIPTION.PROOF.ACTION.PREVIOUS}
                  </Button>

                  <Button type="primary" onClick={handleSubmit}>
                    {SUBSCRIPTION.PROOF.ACTION.SAVE}
                    <MdArrowForwardIos size={16} />
                  </Button>
                </div>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default Proof;
